<template>
  <div class="h-full">
    <BaseTable
      table-id="record-mapping-table"
      :headers="headers"
      :list="mappings"
      :selected-item="selectedRecordMapping"
      is-saving-layout-hidden
      is-search-hidden
      is-refresh-button-hidden
      is-download-button-hidden
      hide-grouping
      clickable
      :is-add-new-hidden="mode === 'view'"
      :edit-mode="mode === 'edit'"
      :add-button-text="$t('representative.recordMapping.addNew')"
      disable-multiselect
      @add-new="addNewMapping"
      @tableItemClicked="onRowClick">
      <template v-if="mappings" #custom="{ item, header }">
        <div class="flex items-center w-full px-1">
          <BaseInput
            v-if="header === 'RecordType' || header === 'Description'"
            type="text"
            color="base"
            class="h-8 bg-white rounded-md"
            v-model="item[header]"
            :disabled="mode === 'view'"
            @update:model-value="inputUpdated(item)" />

          <BaseSelectDropdown
            v-if="header === 'TargetEntity'"
            class="w-full"
            name="targetEntityDropdown"
            size="sm"
            flat
            :items="entityDropdownItems"
            :selected-item="{ value: item.TargetEntity, name: item.TargetEntity }"
            @update:selected="handleTargetEntitySelection($event, item)"
            :mode="mode" />
        </div>
      </template>
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1 w-full">
          <button
            type="button"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            @click="deleteMapping(item)"
            v-if="mode === 'edit'">
            <IconTrashOutline class="w-4 h-4" />
          </button>
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import type { RecordMapping } from '@/types/RecordMapping';
import type { SelectDropdownItem } from '@/types/Base';

const { t: $t } = useI18n();

// v-model declarations
const mappings = defineModel<RecordMapping[]>('mappings', { required: true });
const selectedRecordMapping = defineModel<RecordMapping | undefined>('selectedRecordMapping');

// Regular props
defineProps<{
  mode: 'view' | 'edit';
  entityDropdownItems: SelectDropdownItem[];
}>();

// Table headers configuration
const headers = computed(() => [
  {
    value: 'RecordType',
    text: $t('representative.recordMapping.recordType'),
    type: 'text',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'TargetEntity',
    text: $t('representative.recordMapping.targetEntity'),
    type: 'text',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'Description',
    text: $t('representative.recordMapping.description'),
    type: 'text',
    active: true,
    hasInlineEdit: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), type: 'text', active: true },
]);

// Event handlers
function onRowClick(recordMapping: RecordMapping) {
  selectedRecordMapping.value = recordMapping;
}

function addNewMapping() {
  const newRecordMapping: RecordMapping = {
    RecordType: '',
    TargetEntity: '',
    Description: '',
    FieldMappings: [],
  };

  // Add new mapping at the top of the list
  mappings.value = [newRecordMapping, ...mappings.value];
  selectedRecordMapping.value = newRecordMapping;
}

function deleteMapping(item: RecordMapping) {
  const index = mappings.value.findIndex((mapping) => mapping === item);
  if (index > -1) {
    mappings.value.splice(index, 1);
    if (selectedRecordMapping.value === item) {
      selectedRecordMapping.value = undefined;
    }
  }
}

function inputUpdated(item: RecordMapping) {
  const index = mappings.value.findIndex((mapping) => mapping === item);
  if (index > -1) {
    mappings.value[index] = { ...item };
  }
}

function handleTargetEntitySelection(selectedItem: SelectDropdownItem | null, item: RecordMapping) {
  if (selectedItem) {
    item.TargetEntity = selectedItem.value?.toString() || '';
    inputUpdated(item);
  }
}
</script>
